import { create } from 'zustand'

const clampCount = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0
  }

  const normalized = Math.floor(value)
  return normalized > 0 ? normalized : 0
}

const toKey = (threadId: number | string): string => String(threadId)

export type UnreadState = {
  total: number
  byThread: Record<string, number>
  setTotal: (total: number) => void
  hydrateThreads: (counts: Record<string, number>) => void
  applyNewMessage: (threadId: number | string, delta: number) => void
  applyThreadReadUpTo: (threadId: number | string, newUnreadCount: number) => void
  setThreadCount: (threadId: number | string, count: number) => void
  reset: () => void
}

const useUnreadStore = create<UnreadState>((set) => ({
  total: 0,
  byThread: {},
  setTotal: (total) => {
    const sanitized = clampCount(Number(total))
    set((state) => (state.total === sanitized ? state : { ...state, total: sanitized }))
  },
  hydrateThreads: (counts) => {
    if (!counts || typeof counts !== 'object') {
      return
    }

    const sanitizedEntries = Object.entries(counts).reduce<Record<string, number>>((acc, [rawKey, rawValue]) => {
      const key = toKey(rawKey)
      acc[key] = clampCount(Number(rawValue))
      return acc
    }, {})

    if (!Object.keys(sanitizedEntries).length) {
      return
    }

    set((state) => ({
      ...state,
      byThread: {
        ...state.byThread,
        ...sanitizedEntries,
      },
    }))
  },
  applyNewMessage: (threadId, delta) => {
    if (!Number.isFinite(delta) || delta === 0) {
      return
    }

    const key = toKey(threadId)
    set((state) => {
      const previous = state.byThread[key] ?? 0
      const nextThreadCount = clampCount(previous + delta)
      const nextTotal = clampCount(state.total + delta)

      if (previous === nextThreadCount && state.total === nextTotal) {
        return state
      }

      return {
        total: nextTotal,
        byThread: {
          ...state.byThread,
          [key]: nextThreadCount,
        },
      }
    })
  },
  applyThreadReadUpTo: (threadId, newUnreadCount) => {
    const key = toKey(threadId)
    const sanitized = clampCount(Number(newUnreadCount))

    set((state) => {
      const hasExisting = Object.prototype.hasOwnProperty.call(state.byThread, key)
      const previous = state.byThread[key] ?? 0

      if (previous === sanitized) {
        return state
      }

      if (!hasExisting) {
        return {
          ...state,
          byThread: {
            ...state.byThread,
            [key]: sanitized,
          },
        }
      }

      const delta = sanitized - previous
      const nextTotal = clampCount(state.total + delta)

      return {
        total: nextTotal,
        byThread: {
          ...state.byThread,
          [key]: sanitized,
        },
      }
    })
  },
  setThreadCount: (threadId, count) => {
    const key = toKey(threadId)
    const sanitized = clampCount(Number(count))

    set((state) => {
      if (state.byThread[key] === sanitized) {
        return state
      }

      return {
        ...state,
        byThread: {
          ...state.byThread,
          [key]: sanitized,
        },
      }
    })
  },
  reset: () => {
    set({ total: 0, byThread: {} })
  },
}))

export default useUnreadStore
